import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Dialog } from '@/components-new/Dialog/Dialog';
import { DialogButtons } from '@/components-new/Dialog/styles';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { Input } from '@/components/form/fields/Input/Input';
import { TextArea } from '@/components/form/fields/TextArea/TextArea';
import { DatePicker } from '@/components/form/fields/DatePicker/DatePicker';
import { Notification } from '@/components/Notification/Notification';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { OptionCard, OptionGrid } from '@/bundle/_OrgAdmin/pages/MyVaultPage/styles';
import { RecentActivityRowType, TaskDetailsType, TaskKindType, TaskNotifyType } from '@/types/dashboardTypes';
import { createTask, DASHBOARD_QUERY_KEYS, loadTeam } from '../../api/api';
import { NOTE_PLACEHOLDER, NOTIFY_OPTIONS, TASK_KINDS } from '../../const/const';
import { Mono } from '../../styles';
import { Pills } from '@/components/Pills/Pills';
import { Pill } from '@/components/Pills/styles';

type NextStepModalType = {
  isOpen: boolean;
  source: RecentActivityRowType;
  onClose: () => void;
  onCreated: (task: TaskDetailsType) => void;
};

const toDateString = (date: Date | null) => (date ? date.toISOString().slice(0, 10) : null);

export const NextStepModal = ({ isOpen, source, onClose, onCreated }: NextStepModalType) => {
  const [kind, setKind] = useState<TaskKindType>(source.next_step_kind);
  const [title, setTitle] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [notify, setNotify] = useState<TaskNotifyType>('both');
  const [due, setDue] = useState<Date | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const { data: teamResponse } = useQuery({ queryKey: DASHBOARD_QUERY_KEYS.team, queryFn: loadTeam });
  const team = teamResponse?.body?.results ?? [];
  const assignee = team.find((m) => m.id === assigneeId) ?? team[0];

  const { mutate, isPending } = useMutation({
    mutationKey: ['create_dashboard_task'],
    mutationFn: () =>
      createTask({
        kind,
        title: kind === 'custom' ? title : undefined,
        assigned_to_id: assignee.id,
        notify,
        due_date: toDateString(due),
        note,
        source_event_id: source.id,
        linked_label: source.object_label,
      }),
    onSuccess(response) {
      if (response?.error) return setError(getResponseError(response.error));

      showSnackbar(`Task sent to ${assignee.name}. You will be notified when they finish or ask something.`);
      onCreated(response.body);
    },
  });

  return (
    <Dialog isOpen={isOpen} header='Next step' width='720px' onClose={onClose}>
      <Box display='flex' flexDirection='column' gap='24px'>
        <Box padding='14px 16px' borderRadius='10px' background='#F7F9F8'>
          <Typography variant='caption'>Completed action</Typography>
          <Typography variant='bodySmall'>
            <Mono>{source.event}</Mono> · {source.object_label} · {source.actor_label}
          </Typography>
        </Box>

        <Box>
          <Typography variant='bodySmall' fontWeight='medium' marginBottom='8px'>
            What should happen next?
          </Typography>
          <OptionGrid $cols={2} $gap='10px'>
            {TASK_KINDS.map((item) => (
              <OptionCard key={item.key} type='button' $active={kind === item.key} onClick={() => setKind(item.key)}>
                <Typography fontWeight='semibold'>{item.label}</Typography>
                <Typography variant='caption'>{item.sub}</Typography>
              </OptionCard>
            ))}
          </OptionGrid>
        </Box>

        {kind === 'custom' && (
          <Input
            name='title'
            label='Task title'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder='Task title'
            hasErrorComponent={false}
          />
        )}

        <Box>
          <Typography variant='bodySmall' fontWeight='medium' marginBottom='8px'>
            Assign to
          </Typography>
          <Pills>
            {team.map((member) => (
              <Pill
                key={member.id}
                type='button'
                $active={assignee?.id === member.id}
                onClick={() => setAssigneeId(member.id)}
              >
                <Box textAlign='left'>
                  {member.name}
                  <Typography variant='bodySmall2'>{member.email}</Typography>
                </Box>
              </Pill>
            ))}
            {team.length === 0 && (
              <Typography variant='caption'>No other users yet. Invite your team in Organization details.</Typography>
            )}
          </Pills>
        </Box>

        <Box display='grid' gridTemplateColumns='1fr 1fr' gap='14px'>
          <DatePicker label='Due' value={due} minDate={new Date()} onChange={setDue} placeholder='Select a date' />
          <Box>
            <Typography variant='bodySmall' fontWeight='medium' marginBottom='8px'>
              Notify me when
            </Typography>
            <Pills>
              {NOTIFY_OPTIONS.map((item) => (
                <Pill key={item.key} type='button' $active={notify === item.key} onClick={() => setNotify(item.key)}>
                  {item.label}
                </Pill>
              ))}
            </Pills>
          </Box>
        </Box>

        <TextArea
          name='note'
          label='Note to assignee'
          placeholder={NOTE_PLACEHOLDER[kind]}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {error && <Notification variant='error'>{error}</Notification>}
      </Box>

      <DialogButtons>
        <ButtonNew variant='secondary' onClick={onClose}>
          Cancel
        </ButtonNew>
        <ButtonNew
          isLoading={isPending}
          disabled={!assignee || (kind === 'custom' && !title.trim() && !note.trim())}
          onClick={() => mutate()}
        >
          Notify and create task
        </ButtonNew>
      </DialogButtons>
    </Dialog>
  );
};
